import { useEffect, useState } from 'react';
import {
  Button,
  FormControl,
  FormLabel,
  Input,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Select,
  Text,
  useToast,
} from '@chakra-ui/react';
import { updateBeneficiaryStatus } from 'api/beneficiaries';
import { TOAST_STATUS_SUCCESS } from 'constants/constants';
import { capitalize } from 'lodash';
import { BENEFICIARY_UPDATABLE_STATUS_OPTIONS } from 'modules/beneficiaries/constants/constants';
import { formatBeneficiaryStatus } from 'modules/beneficiaries/utils/utils';
import { showError, showToast } from 'utils/notifications';

const UpdateBeneficiaryStatusModal = ({ isOpen, onClose, beneficiary, onUpdated }) => {
  const [selectedStatus, setSelectedStatus] = useState('');
  const [remarks, setRemarks] = useState('');
  const [isUpdating, setIsUpdating] = useState(false);

  const toast = useToast();

  useEffect(() => {
    if (isOpen) {
      setSelectedStatus('');
      setRemarks('');
      setIsUpdating(false);
    }
  }, [isOpen]);

  const handleModalClose = () => {
    setIsUpdating(false);
    onClose();
  };

  const handleUpdate = async () => {
    if (!selectedStatus) return;

    setIsUpdating(true);
    try {
      await updateBeneficiaryStatus(beneficiary?.id, { status: selectedStatus, remarks });

      showToast(
        toast,
        capitalize(TOAST_STATUS_SUCCESS),
        `Beneficiary moved to ${formatBeneficiaryStatus(selectedStatus)}`,
        TOAST_STATUS_SUCCESS
      );
      onUpdated?.();
      handleModalClose();
    } catch (error) {
      showError(toast, 'Failed to update beneficiary status', error, 'Failed to update beneficiary status');
      setIsUpdating(false);
    }
  };

  const statusOptions = BENEFICIARY_UPDATABLE_STATUS_OPTIONS.filter((option) => option.value !== beneficiary?.status);

  return (
    <Modal isOpen={isOpen} onClose={handleModalClose} isCentered>
      <ModalOverlay />
      <ModalContent borderRadius='lg'>
        <ModalHeader>Update Status</ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          <Text fontSize='sm' color='gray.600' mb={3}>
            Current status: <strong>{formatBeneficiaryStatus(beneficiary?.status)}</strong>
          </Text>
          <FormControl>
            <FormLabel>New Status</FormLabel>
            <Select
              placeholder='Select a status'
              value={selectedStatus}
              onChange={(event) => setSelectedStatus(event.target.value)}
            >
              {statusOptions.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </Select>
            <FormLabel mt={4}>Remarks</FormLabel>
            <Input value={remarks} onChange={(event) => setRemarks(event.target.value)} placeholder='Enter remarks' />
          </FormControl>
        </ModalBody>
        <ModalFooter>
          <Button variant='ghost' mr={3} onClick={handleModalClose}>
            Cancel
          </Button>
          <Button colorScheme='blue' isDisabled={!selectedStatus} isLoading={isUpdating} onClick={handleUpdate}>
            Update
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export default UpdateBeneficiaryStatusModal;
